/**
 * CONTACT-MGMT (CM-5) — Contacts address-book list (`/admin/contacts`).
 *
 * Mirrors the Users admin page (URL-search-param-driven, plain semantic
 * table). Columns (CM-D16): Company name · City · Country · VAT · Used-count
 * (used-count links to the contact's where-used on the detail page). All-text
 * debounced search (CM-D9), default sort companyName A–Z, page size 25, a
 * show-deleted toggle (CM-D15) with a Restore action on deleted rows. Add /
 * Restore are gated to the contact write roles (CM-D1).
 */
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeading, DataPageFooter } from "@aeriosiq/ui";
import { countryByCode } from "../../sites/shared/iso3166.js";
import { ToastProvider, useToast } from "../../account/ToastContext.js";
import { ContactForm } from "./ContactForm.js";
import {
  useContactsListQuery,
  useRestoreContactMutation,
} from "./useContacts.js";
import { useContactWriteAccess } from "./useContactPermissions.js";
import type {
  ContactListItem,
  ContactsListParams,
  ContactsSortDir,
  ContactsSortKey,
} from "./types.js";

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

export function ContactsListPage(): ReactElement {
  return (
    <ToastProvider>
      <ContactsListPageInner />
    </ToastProvider>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, ms]);
  return debounced;
}

function ContactsListPageInner(): ReactElement {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { canCreate, canDelete } = useContactWriteAccess();
  const { toast } = useToast();
  const restore = useRestoreContactMutation();

  const [addOpen, setAddOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(params.get("q") ?? "");
  const debouncedSearch = useDebounced(searchInput, 400);

  // react-router-dom v7's `setSearchParams` returns a NEW reference on every
  // URL change (its useCallback closes over the current params), so this
  // effect — which depends on `setParams` — would re-fire on EVERY URL
  // mutation, including pagination clicks. Its `next.delete("page")` then
  // strips the page the user just set, making pagination appear stuck on
  // page 1. Guard with a ref so the body only runs when the debounced search
  // ACTUALLY changed (mirrors SitesPage).
  const lastAppliedSearchRef = useRef(params.get("q") ?? "");

  useEffect(() => {
    if (lastAppliedSearchRef.current === debouncedSearch) return;
    lastAppliedSearchRef.current = debouncedSearch;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) next.set("q", debouncedSearch);
        else next.delete("q");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, setParams]);

  const includeDeleted = params.get("includeDeleted") === "true";
  const sortBy =
    (params.get("sortBy") as ContactsSortKey | null) ?? "companyName";
  const sortDir = (params.get("sortDir") as ContactsSortDir | null) ?? "asc";
  const page = Number(params.get("page") ?? "1");
  const pageSizeRaw = Number(params.get("pageSize"));
  const pageSize = (PAGE_SIZE_OPTIONS as readonly number[]).includes(
    pageSizeRaw,
  )
    ? pageSizeRaw
    : DEFAULT_PAGE_SIZE;

  const queryParams = useMemo<ContactsListParams>(
    () => ({
      ...(debouncedSearch ? { q: debouncedSearch } : {}),
      includeDeleted,
      sortBy,
      sortDir,
      page,
      pageSize,
    }),
    [debouncedSearch, includeDeleted, sortBy, sortDir, page, pageSize],
  );

  const { data, isPending, isError } = useContactsListQuery(queryParams);

  function toggleSort(key: ContactsSortKey): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const curKey = next.get("sortBy") ?? "companyName";
        const curDir = next.get("sortDir") ?? "asc";
        if (curKey === key) {
          next.set("sortDir", curDir === "asc" ? "desc" : "asc");
        } else {
          next.set("sortBy", key);
          next.set("sortDir", "asc");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  function setIncludeDeleted(checked: boolean): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (checked) next.set("includeDeleted", "true");
        else next.delete("includeDeleted");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  async function handleRestore(id: string): Promise<void> {
    try {
      await restore.mutateAsync(id);
      toast("Contact restored.");
    } catch {
      toast("Couldn't restore the contact.", "error");
    }
  }

  return (
    <Box sx={{ pb: 6 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <PageHeading>Contacts</PageHeading>
        {canCreate ? (
          <Button
            variant="contained"
            onClick={() => {
              setAddOpen(true);
            }}
          >
            Add contact
          </Button>
        ) : null}
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2, alignItems: "center" }}
      >
        <TextField
          slotProps={{
            htmlInput: {
              "aria-label": "Search contacts",
              type: "search",
            },
          }}
          placeholder="Search by company, city, VAT…"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
          }}
          size="small"
          sx={{ width: 360 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={includeDeleted}
              onChange={(_e, checked) => {
                setIncludeDeleted(checked);
              }}
              inputProps={{ "aria-label": "Show deleted contacts" }}
            />
          }
          label="Show deleted"
        />
      </Stack>

      {isPending ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">Couldn&apos;t load contacts.</Typography>
      ) : (
        <ContactsGrid
          contacts={data.items}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={toggleSort}
          onOpen={(id) => {
            void navigate(`/admin/contacts/${id}`);
          }}
          canRestore={canDelete}
          onRestore={(id) => {
            void handleRestore(id);
          }}
          restorePending={restore.isPending}
        />
      )}

      {!isPending && !isError ? (
        <DataPageFooter
          total={data.total}
          entityLabel="contacts"
          page={page}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageChange={(p) => {
            setParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set("page", String(p));
                return next;
              },
              { replace: true },
            );
          }}
          onPageSizeChange={(size) => {
            setParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set("pageSize", String(size));
                // Reset to page 1 — the current page may not exist at the
                // new size (mirrors SitesPage's setPageSize behaviour).
                next.set("page", "1");
                return next;
              },
              { replace: true },
            );
          }}
          totalTestId="contacts-footer-total"
        />
      ) : null}

      <Dialog
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add contact</DialogTitle>
        <DialogContent>
          <ContactForm
            mode="create"
            onCancel={() => {
              setAddOpen(false);
            }}
            onSaved={(contact) => {
              setAddOpen(false);
              toast("Contact created.");
              void navigate(`/admin/contacts/${contact.contactId}`);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

interface ContactsGridProps {
  contacts: ContactListItem[];
  sortBy: ContactsSortKey;
  sortDir: ContactsSortDir;
  onSort: (key: ContactsSortKey) => void;
  onOpen: (id: string) => void;
  canRestore: boolean;
  onRestore: (id: string) => void;
  restorePending: boolean;
}

function ContactsGrid({
  contacts,
  sortBy,
  sortDir,
  onSort,
  onOpen,
  canRestore,
  onRestore,
  restorePending,
}: ContactsGridProps): ReactElement {
  if (contacts.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
        <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
          No contacts match
        </Typography>
        <Typography variant="body2">
          Try clearing your search or adding a new contact.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="table"
      role="grid"
      aria-label="Contacts in your tenant"
      sx={{
        width: "100%",
        borderCollapse: "collapse",
        "& th, & td": {
          textAlign: "left",
          py: 1.5,
          px: 1.5,
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          fontSize: 14,
        },
        "& th": {
          color: "text.secondary",
          fontWeight: 500,
          fontSize: 12,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        },
        "& tr[role='row']": {
          cursor: "pointer",
          transition: "background-color 120ms",
        },
        "& tr[role='row']:hover": {
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? "rgba(255,255,255,0.04)"
              : "rgba(0,0,0,0.02)",
        },
      }}
    >
      <thead>
        <tr role="row">
          <SortHeader
            label="Company name"
            sortKey="companyName"
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={onSort}
          />
          <SortHeader
            label="City"
            sortKey="city"
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={onSort}
          />
          <SortHeader
            label="Country"
            sortKey="countryCode"
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={onSort}
          />
          <SortHeader
            label="VAT"
            sortKey="vatId"
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={onSort}
          />
          <th role="columnheader" scope="col">
            Used
          </th>
          <th role="columnheader" scope="col" aria-label="Actions"></th>
        </tr>
      </thead>
      <tbody>
        {contacts.map((c) => {
          const country = countryByCode(c.countryCode);
          const rowName = `${c.companyName ?? "—"} ${c.city ?? ""} ${
            c.vatId ?? ""
          }`.trim();
          return (
            <tr
              key={c.contactId}
              role="row"
              aria-label={rowName}
              tabIndex={0}
              onClick={() => {
                onOpen(c.contactId);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onOpen(c.contactId);
                }
              }}
              data-contact-id={c.contactId}
              data-deleted={c.isDeleted ? "true" : "false"}
            >
              <td>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      ...(c.isDeleted
                        ? { textDecoration: "line-through", opacity: 0.6 }
                        : {}),
                    }}
                  >
                    {c.companyName ?? "—"}
                  </Typography>
                  {c.isDeleted ? (
                    <Chip label="Deleted" size="small" color="default" />
                  ) : null}
                </Stack>
              </td>
              <td>{c.city ?? "—"}</td>
              <td>
                <Box component="span" title={country?.name ?? undefined}>
                  {c.countryCode ?? "—"}
                </Box>
              </td>
              <td>{c.vatId ?? "—"}</td>
              <td>
                {/* Used-count links to where-used (the detail page). */}
                <Box
                  component="span"
                  role="link"
                  tabIndex={0}
                  aria-label={`Used in ${String(c.usedCount)} place(s)`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(c.contactId);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onOpen(c.contactId);
                    }
                  }}
                  sx={{
                    cursor: "pointer",
                    color: c.usedCount > 0 ? "primary.main" : "text.secondary",
                    textDecoration: c.usedCount > 0 ? "underline" : "none",
                  }}
                >
                  {c.usedCount}
                </Box>
              </td>
              <td style={{ textAlign: "right" }}>
                {c.isDeleted && canRestore ? (
                  <Button
                    size="small"
                    variant="text"
                    disabled={restorePending}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRestore(c.contactId);
                    }}
                  >
                    Restore
                  </Button>
                ) : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </Box>
  );
}

interface SortHeaderProps {
  label: string;
  sortKey: ContactsSortKey;
  sortBy: ContactsSortKey;
  sortDir: ContactsSortDir;
  onSort: (key: ContactsSortKey) => void;
}

function SortHeader({
  label,
  sortKey,
  sortBy,
  sortDir,
  onSort,
}: SortHeaderProps): ReactElement {
  const active = sortBy === sortKey;
  return (
    <th
      role="columnheader"
      scope="col"
      aria-sort={
        active ? (sortDir === "asc" ? "ascending" : "descending") : "none"
      }
    >
      <Box
        component="button"
        type="button"
        onClick={() => {
          onSort(sortKey);
        }}
        sx={{
          all: "unset",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          font: "inherit",
          color: "inherit",
          textTransform: "inherit",
          letterSpacing: "inherit",
        }}
      >
        {label}
        {active ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
      </Box>
    </th>
  );
}
